/**
 * Semantic Intelligence Block (SIB) Schema
 * 
 * Blocks are the atomic rendering units of the Intelligence Cockpit.
 * Each block type has a well-defined schema for its data and configuration.
 * 
 * @version 2.2
 */

import { z } from 'zod'
import {
  ConfidenceLevelSchema,
  RiskPostureSchema,
  EvidenceAnchorSchema,
  MetricValueSchema,
  ScenarioBindingSchema,
  FigureTypeSchema,
} from './cig'

// ============================================================================
// Block Base Schema
// ============================================================================

export const BlockBaseSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string().optional(),
  collapsed: z.boolean().optional().default(false),
  priority: z.number().optional(), // for persona-based ordering
  scenario_binding: ScenarioBindingSchema.optional(),
})

// ============================================================================
// Signal Blocks
// ============================================================================

export const StatusStripBlockSchema = BlockBaseSchema.extend({
  type: z.literal('status_strip'),
  data: z.object({
    risk_posture: RiskPostureSchema,
    confidence: ConfidenceLevelSchema,
    last_updated: z.string(),
    what_changed: z.string().optional(),
    tags: z.array(z.string()).optional(),
  }),
})
export type StatusStripBlock = z.infer<typeof StatusStripBlockSchema>

export const SignalCardBlockSchema = BlockBaseSchema.extend({
  type: z.literal('signal_card'),
  data: z.union([
    // Numeric signal
    z.object({
      variant: z.literal('numeric'),
      value: MetricValueSchema,
      unit: z.string().optional(),
      timeframe: z.string().optional(),
      trend: z.enum(['up', 'down', 'stable', 'volatile']).optional(),
      trend_value: z.number().optional(),
      confidence: ConfidenceLevelSchema,
      evidence_anchors: z.array(EvidenceAnchorSchema),
    }),
    // Categorical signal
    z.object({
      variant: z.literal('categorical'),
      value: z.string(), // e.g., "Holding", "Bullish", "At Risk"
      color: z.enum(['green', 'yellow', 'red', 'blue', 'gray']).optional(),
      confidence: ConfidenceLevelSchema,
      evidence_anchors: z.array(EvidenceAnchorSchema),
    }),
  ]),
})
export type SignalCardBlock = z.infer<typeof SignalCardBlockSchema>

export const VerdictBlockSchema = BlockBaseSchema.extend({
  type: z.literal('verdict'),
  data: z.object({
    bluf: z.string(), // Bottom Line Up Front - the main verdict
    so_what: z.string(), // Why this matters
    counterpoint: z.string().optional(), // Key opposing view
    key_assumption: z.string().optional(), // The critical assumption
    confidence: ConfidenceLevelSchema,
    evidence_anchors: z.array(EvidenceAnchorSchema),
  }),
})
export type VerdictBlock = z.infer<typeof VerdictBlockSchema>

// ============================================================================
// Understanding Blocks
// ============================================================================

export const ClaimCardBlockSchema = BlockBaseSchema.extend({
  type: z.literal('claim_card'),
  data: z.object({
    claim: z.string(),
    why_it_matters: z.string().optional(),
    counterpoint: z.string().optional(),
    confidence: ConfidenceLevelSchema,
    evidence_anchors: z.array(EvidenceAnchorSchema),
    related_entity_ids: z.array(z.string()).optional(),
  }),
})
export type ClaimCardBlock = z.infer<typeof ClaimCardBlockSchema>

export const CausalNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  type: z.enum(['cause', 'effect', 'mechanism', 'condition']).optional(),
})
export type CausalNode = z.infer<typeof CausalNodeSchema>

export const CausalEdgeSchema = z.object({
  source: z.string(),
  target: z.string(),
  label: z.string().optional(),
  strength: z.number().min(0).max(1).optional(),
})
export type CausalEdge = z.infer<typeof CausalEdgeSchema>

export const CausalChainBlockSchema = BlockBaseSchema.extend({
  type: z.literal('causal_chain'),
  data: z.object({
    nodes: z.array(CausalNodeSchema),
    edges: z.array(CausalEdgeSchema),
    layout: z.enum(['horizontal', 'vertical', 'tree', 'sankey']).optional(),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type CausalChainBlock = z.infer<typeof CausalChainBlockSchema>

export const TimelineEventSchema = z.object({
  id: z.string(),
  date: z.string(),
  title: z.string(),
  description: z.string().optional(),
  category: z.string().optional(),
  importance: z.enum(['high', 'medium', 'low']).optional(),
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
})
export type TimelineEvent = z.infer<typeof TimelineEventSchema>

export const TimelineBlockSchema = BlockBaseSchema.extend({
  type: z.literal('timeline'),
  data: z.object({
    events: z.array(TimelineEventSchema),
    categories: z.array(z.string()).optional(),
    date_range: z.object({
      start: z.string().optional(),
      end: z.string().optional(),
    }).optional(),
  }),
})
export type TimelineBlock = z.infer<typeof TimelineBlockSchema>

// IRAC-style issue tree for legal/argument analysis
// Define the base type first to handle recursion
interface IssueNodeType {
  id: string
  type: 'issue' | 'rule' | 'application' | 'conclusion' | 'sub_issue'
  content: string
  children?: IssueNodeType[]
  evidence_anchors?: Array<{
    source_id: string
    locator?: string
    snippet?: string
    support_type?: 'supports' | 'contradicts' | 'context'
    reliability_score?: number
    timestamp?: string
  }>
  strength?: 'strong' | 'moderate' | 'weak'
}

// Use z.lazy for recursive type with explicit typing
const baseIssueNodeSchema = z.object({
  id: z.string(),
  type: z.enum(['issue', 'rule', 'application', 'conclusion', 'sub_issue']),
  content: z.string(),
  evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  strength: z.enum(['strong', 'moderate', 'weak']).optional(),
})

export const IssueNodeSchema: z.ZodType<IssueNodeType> = baseIssueNodeSchema.extend({
  children: z.lazy(() => z.array(IssueNodeSchema)).optional(),
})
export type IssueNode = IssueNodeType

export const IssueTreeBlockSchema = BlockBaseSchema.extend({
  type: z.literal('issue_tree'),
  data: z.object({
    root: IssueNodeSchema,
    verdict: z.string().optional(),
  }),
})
export type IssueTreeBlock = z.infer<typeof IssueTreeBlockSchema>

export const GlossaryTermSchema = z.object({
  term: z.string(),
  definition: z.string(),
  aliases: z.array(z.string()).optional(),
  related_terms: z.array(z.string()).optional(),
})
export type GlossaryTerm = z.infer<typeof GlossaryTermSchema>

export const GlossaryBlockSchema = BlockBaseSchema.extend({
  type: z.literal('glossary'),
  data: z.object({
    terms: z.array(GlossaryTermSchema),
    show_inline_chips: z.boolean().optional().default(true),
  }),
})
export type GlossaryBlock = z.infer<typeof GlossaryBlockSchema>

// ============================================================================
// Exploration Blocks
// ============================================================================

export const VizFrameBlockSchema = BlockBaseSchema.extend({
  type: z.literal('viz_frame'),
  data: z.object({
    renderer: FigureTypeSchema,
    figure_id: z.string().optional(), // Reference to a Figure in the CIG
    inline_data: z.unknown().optional(), // Or inline chart data
    caption: z.string(),
    sources: z.array(z.string()).optional(), // Source IDs
    interactions: z.array(z.enum(['zoom', 'pan', 'select', 'filter', 'hover'])).optional(),
    scenario_data_key: z.string().optional(), // Key for scenario-bound data switching
    render_options: z.record(z.unknown()).optional(),
  }),
})
export type VizFrameBlock = z.infer<typeof VizFrameBlockSchema>

export const DataTableColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(['string', 'number', 'date', 'boolean', 'currency', 'percentage', 'link']),
  sortable: z.boolean().optional().default(true),
  filterable: z.boolean().optional().default(true),
  format: z.string().optional(),
  width: z.number().optional(),
  evidence_source_id: z.string().optional(), // Citation for this column
})
export type DataTableColumn = z.infer<typeof DataTableColumnSchema>

export const DataTableBlockSchema = BlockBaseSchema.extend({
  type: z.literal('data_table'),
  data: z.object({
    dataset_id: z.string().optional(), // Reference to Dataset in CIG
    columns: z.array(DataTableColumnSchema).optional(),
    rows: z.array(z.record(z.unknown())).optional(),
    page_size: z.number().optional().default(10),
    enable_export: z.boolean().optional().default(true),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type DataTableBlock = z.infer<typeof DataTableBlockSchema>

export const EntityProfileBlockSchema = BlockBaseSchema.extend({
  type: z.literal('entity_profile'),
  data: z.object({
    entity_id: z.string(),
    name: z.string(),
    type: z.string(),
    description: z.string().optional(),
    key_metrics: z.array(z.object({
      label: z.string(),
      value: MetricValueSchema,
      unit: z.string().optional(),
    })).optional(),
    relationships: z.array(z.object({
      type: z.string(),
      target_entity_id: z.string(),
      target_entity_name: z.string(),
    })).optional(),
    mentioned_in_sections: z.array(z.string()).optional(),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type EntityProfileBlock = z.infer<typeof EntityProfileBlockSchema>

export const ComparisonGridBlockSchema = BlockBaseSchema.extend({
  type: z.literal('comparison_grid'),
  data: z.object({
    entities: z.array(z.object({
      id: z.string(),
      name: z.string(),
      image_url: z.string().optional(),
    })),
    metrics: z.array(z.object({
      key: z.string(),
      label: z.string(),
      unit: z.string().optional(),
      format: z.string().optional(),
      higher_is_better: z.boolean().optional(),
    })),
    values: z.record(z.string(), z.record(z.string(), MetricValueSchema)), // entity_id -> metric_key -> value
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type ComparisonGridBlock = z.infer<typeof ComparisonGridBlockSchema>

// ============================================================================
// Action Blocks
// ============================================================================

export const DecisionOptionsBlockSchema = BlockBaseSchema.extend({
  type: z.literal('decision_options'),
  data: z.object({
    options: z.array(z.object({
      id: z.string(),
      label: z.string(),
      description: z.string(),
      pros: z.array(z.string()),
      cons: z.array(z.string()),
      expected_outcomes: z.array(z.string()).optional(),
      risks: z.array(z.string()).optional(),
      evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
    })),
    recommendation: z.string().optional(),
    recommendation_rationale: z.string().optional(),
  }),
})
export type DecisionOptionsBlock = z.infer<typeof DecisionOptionsBlockSchema>

export const WatchlistBlockSchema = BlockBaseSchema.extend({
  type: z.literal('watchlist'),
  data: z.object({
    items: z.array(z.object({
      id: z.string(),
      indicator: z.string(),
      description: z.string().optional(),
      threshold: z.string().optional(),
      current_value: MetricValueSchema.optional(),
      status: z.enum(['normal', 'warning', 'critical']).optional(),
      maps_to_scenario_state: z.string().optional(),
      evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
    })),
    show_scenario_mapping: z.boolean().optional().default(true),
  }),
})
export type WatchlistBlock = z.infer<typeof WatchlistBlockSchema>

export const ChecklistItemSchema = z.object({
  id: z.string(),
  text: z.string(),
  completed: z.boolean().optional().default(false),
  notes: z.string().optional(),
  category: z.string().optional(),
})
export type ChecklistItem = z.infer<typeof ChecklistItemSchema>

export const ChecklistBlockSchema = BlockBaseSchema.extend({
  type: z.literal('checklist'),
  data: z.object({
    items: z.array(ChecklistItemSchema),
    categories: z.array(z.string()).optional(),
    allow_user_edits: z.boolean().optional().default(true),
  }),
})
export type ChecklistBlock = z.infer<typeof ChecklistBlockSchema>

// ============================================================================
// Trust Blocks
// ============================================================================

export const EvidenceBundleBlockSchema = BlockBaseSchema.extend({
  type: z.literal('evidence_bundle'),
  data: z.object({
    sources: z.array(z.object({
      id: z.string(),
      title: z.string(),
      publisher: z.string().optional(),
      date: z.string().optional(),
      url: z.string().optional(),
      reliability_score: z.number().min(0).max(1),
      reliability_rationale: z.string(),
      excerpts: z.array(z.object({
        id: z.string(),
        text: z.string(),
        page: z.number().optional(),
      })).optional(),
      pinned: z.boolean().optional().default(false),
    })),
    filter_by_claim_id: z.string().optional(),
    show_reliability_scores: z.boolean().optional().default(true),
  }),
})
export type EvidenceBundleBlock = z.infer<typeof EvidenceBundleBlockSchema>

export const AssumptionsBlockSchema = BlockBaseSchema.extend({
  type: z.literal('assumptions'),
  data: z.object({
    assumptions: z.array(z.object({
      id: z.string(),
      text: z.string(),
      type: z.enum(['scenario', 'model', 'definition', 'constraint']).optional(),
      applies_to_state: z.string().optional(),
      evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
    })),
    show_scenario_grouping: z.boolean().optional().default(true),
  }),
})
export type AssumptionsBlock = z.infer<typeof AssumptionsBlockSchema>

export const LimitationsBlockSchema = BlockBaseSchema.extend({
  type: z.literal('limitations'),
  data: z.object({
    limitations: z.array(z.object({
      id: z.string(),
      text: z.string(),
      severity: z.enum(['major', 'moderate', 'minor']).optional(),
      mitigation: z.string().optional(),
    })),
    open_questions: z.array(z.string()).optional(),
  }),
})
export type LimitationsBlock = z.infer<typeof LimitationsBlockSchema>

export const ChangeLogBlockSchema = BlockBaseSchema.extend({
  type: z.literal('change_log'),
  data: z.object({
    entries: z.array(z.object({
      id: z.string(),
      date: z.string(),
      type: z.enum(['added', 'updated', 'removed', 'corrected']),
      description: z.string(),
      affected_sections: z.array(z.string()).optional(),
    })),
  }),
})
export type ChangeLogBlock = z.infer<typeof ChangeLogBlockSchema>

// ============================================================================
// Utility Blocks
// ============================================================================

export const JumpChipsBlockSchema = BlockBaseSchema.extend({
  type: z.literal('jump_chips'),
  data: z.object({
    chips: z.array(z.object({
      label: z.string(),
      target_section_id: z.string().optional(),
      target_tab: z.string().optional(),
      target_entity_id: z.string().optional(),
      icon: z.string().optional(),
    })),
  }),
})
export type JumpChipsBlock = z.infer<typeof JumpChipsBlockSchema>

export const SectionHeaderBlockSchema = BlockBaseSchema.extend({
  type: z.literal('section_header'),
  data: z.object({
    title: z.string(),
    subtitle: z.string().optional(),
    level: z.number().min(1).max(3).optional().default(2),
  }),
})
export type SectionHeaderBlock = z.infer<typeof SectionHeaderBlockSchema>

// ============================================================================
// SIB-Specific Blocks (from Structured Intelligence Blocks)
// ============================================================================

export const ComparisonMatrixBlockSchema = BlockBaseSchema.extend({
  type: z.literal('comparison_matrix'),
  data: z.object({
    row_headers: z.array(z.string()),
    column_headers: z.array(z.string()),
    cells: z.array(z.array(z.unknown())),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type ComparisonMatrixBlock = z.infer<typeof ComparisonMatrixBlockSchema>

export const RiskMatrixBlockSchema = BlockBaseSchema.extend({
  type: z.literal('risk_matrix'),
  data: z.object({
    risks: z.array(z.object({
      id: z.string().optional(),
      name: z.string(),
      description: z.string().optional(),
      likelihood: z.enum(['very_low', 'low', 'medium', 'high', 'very_high']).optional(),
      impact: z.enum(['very_low', 'low', 'medium', 'high', 'very_high']).optional(),
      category: z.string().optional(),
      mitigation: z.string().optional(),
    })),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type RiskMatrixBlock = z.infer<typeof RiskMatrixBlockSchema>

export const RatingScorecardBlockSchema = BlockBaseSchema.extend({
  type: z.literal('rating_scorecard'),
  data: z.object({
    subject: z.string(),
    dimensions: z.array(z.object({
      id: z.string().optional(),
      name: z.string(),
      score: z.number(),
      max_score: z.number().optional(),
      weight: z.number().optional(),
      rationale: z.string().optional(),
    })),
    overall_score: z.number(),
    overall_max: z.number().optional(),
    grade: z.string().optional(),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type RatingScorecardBlock = z.infer<typeof RatingScorecardBlockSchema>

export const ScenarioComparisonBlockSchema = BlockBaseSchema.extend({
  type: z.literal('scenario_comparison'),
  data: z.object({
    scenarios: z.array(z.object({
      id: z.string(),
      name: z.string(),
      description: z.string().optional(),
      probability: z.number().optional(),
      outcomes: z.array(z.object({
        metric: z.string(),
        value: z.unknown(),
        unit: z.string().optional(),
      })).optional(),
    })),
    axis_id: z.string().optional(),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type ScenarioComparisonBlock = z.infer<typeof ScenarioComparisonBlockSchema>

export const SensitivityAnalysisBlockSchema = BlockBaseSchema.extend({
  type: z.literal('sensitivity_analysis'),
  data: z.object({
    factors: z.array(z.object({
      id: z.string().optional(),
      name: z.string(),
      baseline_value: z.unknown().optional(),
      range_low: z.unknown().optional(),
      range_high: z.unknown().optional(),
      impact_on_outcome: z.string().optional(),
      sensitivity: z.enum(['very_low', 'low', 'medium', 'high', 'very_high']).optional(),
    })),
    baseline_state_id: z.string().optional(),
    evidence_anchors: z.array(EvidenceAnchorSchema).optional(),
  }),
})
export type SensitivityAnalysisBlock = z.infer<typeof SensitivityAnalysisBlockSchema>

/**
 * Generic SIB Block - Flexible container for SIB types without dedicated schemas
 * Used for SWOT, PESTEL, Quote Galleries, Action Items, Assumptions, Org Charts, etc.
 */
export const GenericSIBBlockSchema = BlockBaseSchema.extend({
  type: z.literal('generic_sib'),
  data: z.object({
    sib_type: z.string(), // The specific SIB type (e.g., 'swot_analyses', 'pestel_analyses')
    content: z.any(), // Flexible content that varies by SIB type
  }),
})
export type GenericSIBBlock = z.infer<typeof GenericSIBBlockSchema>

// ============================================================================
// Union of All Block Types
// ============================================================================

export const BlockSchema = z.discriminatedUnion('type', [
  // Signals
  StatusStripBlockSchema,
  SignalCardBlockSchema,
  VerdictBlockSchema,
  // Understanding
  ClaimCardBlockSchema,
  CausalChainBlockSchema,
  TimelineBlockSchema,
  IssueTreeBlockSchema,
  GlossaryBlockSchema,
  // Exploration
  VizFrameBlockSchema,
  DataTableBlockSchema,
  EntityProfileBlockSchema,
  ComparisonGridBlockSchema,
  // Action
  DecisionOptionsBlockSchema,
  WatchlistBlockSchema,
  ChecklistBlockSchema,
  // Trust
  EvidenceBundleBlockSchema,
  AssumptionsBlockSchema,
  LimitationsBlockSchema,
  ChangeLogBlockSchema,
  // Utility
  JumpChipsBlockSchema,
  SectionHeaderBlockSchema,
  // SIB-Specific
  ComparisonMatrixBlockSchema,
  RiskMatrixBlockSchema,
  RatingScorecardBlockSchema,
  ScenarioComparisonBlockSchema,
  SensitivityAnalysisBlockSchema,
  // Generic SIB for flexible content
  GenericSIBBlockSchema,
])
export type Block = z.infer<typeof BlockSchema>

export type BlockType = Block['type']

// ============================================================================
// Block Registry Helper
// ============================================================================

export const BLOCK_CATEGORIES = {
  signals: ['status_strip', 'signal_card', 'verdict'],
  understanding: ['claim_card', 'causal_chain', 'timeline', 'issue_tree', 'glossary'],
  exploration: ['viz_frame', 'data_table', 'entity_profile', 'comparison_grid', 'comparison_matrix', 'scenario_comparison'],
  action: ['decision_options', 'watchlist', 'checklist'],
  trust: ['evidence_bundle', 'assumptions', 'limitations', 'change_log'],
  utility: ['jump_chips', 'section_header'],
  analysis: ['risk_matrix', 'rating_scorecard', 'sensitivity_analysis', 'generic_sib'],
} as const

export function getBlockCategory(type: BlockType): keyof typeof BLOCK_CATEGORIES | undefined {
  for (const [category, types] of Object.entries(BLOCK_CATEGORIES)) {
    if ((types as readonly string[]).includes(type)) {
      return category as keyof typeof BLOCK_CATEGORIES
    }
  }
  return undefined
}



